<template>
<div class="order-detail-page">
    <van-nav-bar title="订单详情" fixed :placeholder="true" left-arrow @click-left="goBack" />

    <main class="order-detail-content">
        <section class="order-summary-card">
            <div class="summary-header">
                <div class="summary-heading">
                    <h2>{{ order.cinema_name || '影院信息' }}</h2>
                    <div class="order-id-row">
                        <span>{{ order.order_id }}</span>
                        <button type="button" @click="copyOrderId(order.order_id)">复制</button>
                    </div>
                </div>
                <span v-if="order.city_name" class="city-tag">{{ order.city_name }}</span>
            </div>

            <div class="summary-divider"></div>

            <div class="movie-info">
                <van-image class="movie-poster" fit="cover" :src="order.logo + '_200x200'" />
                <div class="movie-detail">
                    <div class="movie-title-row">
                        <h3>{{ order.movie_name }}</h3>
                        <button type="button" @click="copyOrder(order)">复制整单</button>
                    </div>
                    <div class="showtime-row">
                        <span v-if="order.play_date_tag" class="today-tag">{{ order.play_date_tag }}</span>
                        <strong>{{ order.play_time_text }}</strong>
                    </div>
                    <div v-if="order.hall_name" class="hall-name">{{ order.hall_name }}</div>
                    <div class="movie-tags">
                        <span>{{ order.quantity }}张</span>
                        <span v-if="order.seatTag === '1' || order.seatTag === '2'">情侣座</span>
                        <span v-if="order.support_exchange_seat === 'Y'">可调座</span>
                    </div>
                </div>
            </div>

            <div v-if="order.cinema_addr" class="address-row">
                <van-icon name="location-o" />
                <span>{{ order.cinema_addr }}</span>
            </div>
        </section>

        <section class="price-card">
            <div class="price-row">
                <span>市场参考价</span>
                <strong>￥{{ order.channel_price }}/张</strong>
            </div>
            <div class="price-row">
                <span>最高报价</span>
                <strong class="price-limit">￥{{ order.order_price }}</strong>
            </div>
            <div class="price-row">
                <span>参与竞价</span>
                <strong>{{ order.bid_count || 0 }}人</strong>
            </div>
            <div v-if="order.my_bid_price" class="price-row current-price-row">
                <span>当前报价</span>
                <strong>￥{{ order.my_bid_price }}/张</strong>
            </div>
        </section>
    </main>

    <footer class="bid-bar">
        <div class="bid-deadline" :class="{ 'is-expired': isTimeExpired }">
            <span>{{ isTimeExpired ? '本单竞价已结束' : '剩余报价时间' }}</span>
            <van-count-down
                v-if="!isTimeExpired && remaining > 0"
                :time="remaining * 1000"
                format="mm:ss"
                @finish="onTimeExpired"
            />
        </div>

        <div class="bid-actions">
            <van-button
                v-if="order.my_bid_price"
                plain
                class="cancel-bid-btn"
                :loading="cancelling"
                :disabled="isTimeExpired || submitting || cancelling"
                @click="cancelBid"
            >
                取消
            </van-button>

            <label class="bid-field" :class="{ 'is-disabled': isTimeExpired }">
                <span>￥</span>
                <input
                    v-model.trim="bidPrice"
                    type="number"
                    inputmode="decimal"
                    min="0"
                    step="0.01"
                    :max="order.order_price"
                    :placeholder="isTimeExpired ? '已结束' : '输入报价'"
                    :disabled="isTimeExpired || submitting"
                    @keyup.enter="confirmBid"
                />
            </label>

            <van-button
                type="danger"
                class="confirm-bid-btn"
                :loading="submitting"
                :disabled="isTimeExpired || submitting || cancelling"
                loading-text="提交中"
                @click="confirmBid"
            >
                {{ isTimeExpired ? '已结束' : (order.my_bid_price ? '修改报价' : '确认报价') }}
            </van-button>
        </div>
    </footer>
</div>
</template>

<script>
import { Dialog, Toast } from 'vant'
import Api from '@/api-service'

export default {
    name: 'BidOrderDetail',
    data() {
        return {
            order: {},
            bidPrice: '',
            isTimeExpired: false,
            submitting: false,
            cancelling: false
        }
    },
    created() {
        const orderStr = this.$route.query.order
        if (orderStr) {
            try {
                this.setOrder(JSON.parse(decodeURIComponent(orderStr)))
            } catch (error) {
                this.fetchOrderDetail()
            }
        } else {
            this.fetchOrderDetail()
        }
    },
    computed: {
        remaining() {
            const hasDirectRemaining = this.order.remaining !== undefined && this.order.remaining !== null
            const directRemaining = Number(this.order.remaining)
            if (hasDirectRemaining && Number.isFinite(directRemaining)) {
                return Math.max(0, Math.floor(directRemaining))
            }

            const rawExpiry = this.order.expire_time
            if (!rawExpiry) return 0

            let expiry = Number(rawExpiry)
            if (Number.isFinite(expiry)) {
                if (expiry < 1000000000000) expiry *= 1000
            } else {
                expiry = new Date(rawExpiry).getTime()
            }

            if (!Number.isFinite(expiry)) return 0
            return Math.max(0, Math.floor((expiry - Date.now()) / 1000))
        }
    },
    methods: {
        setOrder(order) {
            this.order = order || {}
            this.bidPrice = this.order.my_bid_price || ''
            this.isTimeExpired = this.remaining <= 0
        },
        fetchOrderDetail() {
            Api.getOrderDetail({
                order_id: this.$route.params.orderId
            }).then(res => {
                this.setOrder((res && res.data) || res || {})
            }).catch(() => {
                Toast.fail('订单信息加载失败')
            })
        },
        copyOrderId(orderId) {
            if (!orderId) return
            this.copyText(orderId, '订单号已复制')
        },
        copyOrder(order) {
            const content = `订单号：${order.order_id || ''}\n城市：${order.city_name || ''}\n影院：${order.cinema_name || ''}\n地址：${order.cinema_addr || ''}\n影片：${order.movie_name || ''}\n场次：${order.play_time_text || ''}\n影厅：${order.hall_name || ''}\n市场价：${order.channel_price || ''}`
            this.copyText(content, '整单信息已复制')
        },
        async copyText(content, successMessage) {
            try {
                await navigator.clipboard.writeText(content)
                Toast.success(successMessage)
            } catch (error) {
                Toast.fail('复制失败')
            }
        },
        goBack() {
            if (this.$route.query.from === 'bid-records') {
                this.$router.replace({ path: '/bid-records', query: { t: Date.now() } })
                return
            }
            if (this.order.my_bid_price) {
                this.$router.replace({ path: '/wait-bid', query: { t: Date.now() } })
            } else {
                this.$router.replace({ path: '/pool', query: { t: Date.now() } })
            }
        },
        async cancelBid() {
            if (this.isTimeExpired || this.cancelling) return

            try {
                await Dialog.confirm({
                    title: '取消报价',
                    message: '取消后将无法再次参与该订单竞价，确定取消吗？',
                    confirmButtonText: '确定取消',
                    cancelButtonText: '保留报价',
                    confirmButtonColor: '#e5484d'
                })
                this.cancelling = true
                await Api.unbidOrder({ order_id: this.order.order_id })
                Toast.success('报价已取消')
                this.goBack()
            } catch (error) {
                if (error !== 'cancel') Toast.fail('取消报价失败')
            } finally {
                this.cancelling = false
            }
        },
        onTimeExpired() {
            this.isTimeExpired = true
        },
        async confirmBid() {
            if (this.isTimeExpired || this.submitting) return

            const price = Number(this.bidPrice)
            const limit = Number(this.order.order_price)
            if (!Number.isFinite(price) || price <= 0) {
                Toast('请输入有效报价')
                return
            }
            if (!/^\d+(\.\d{1,2})?$/.test(String(this.bidPrice))) {
                Toast('报价最多保留两位小数')
                return
            }
            if (Number.isFinite(limit) && limit > 0 && price > limit) {
                Toast(`报价不能超过￥${limit}`)
                return
            }

            this.submitting = true
            try {
                await Api.bidOrder({
                    order_id: this.order.order_id,
                    bid_price: price
                })
                Toast.success(this.order.my_bid_price ? '报价已修改' : '报价成功')
                this.goBack()
            } catch (error) {
                Toast.fail((error && error.errorMsg) || '报价失败')
            } finally {
                this.submitting = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.order-detail-page {
    min-height: 100vh;
    background: #f5f7fb;
}

.order-detail-content {
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
    padding: 12px 12px calc(122px + env(safe-area-inset-bottom, 0px));
    box-sizing: border-box;
}

.order-summary-card,
.price-card {
    overflow: hidden;
    border: 1px solid #e8ecf2;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(28, 42, 68, 0.06);
}

.order-summary-card {
    padding: 15px;
}

.summary-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.summary-heading {
    min-width: 0;
    flex: 1;

    h2 {
        margin: 0;
        overflow: hidden;
        color: #1d2735;
        font-size: 17px;
        line-height: 1.4;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.order-id-row {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 6px;
    margin-top: 5px;
    color: #8793a3;
    font-size: 12px;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    button,
    .movie-title-row button {
        border: 0;
        background: transparent;
    }

    button {
        flex: none;
        padding: 5px 6px;
        color: #5b5bd6;
        font-size: 12px;
    }
}

.city-tag {
    flex: none;
    padding: 5px 8px;
    border-radius: 7px;
    background: #f1f3f7;
    color: #536171;
    font-size: 12px;
}

.summary-divider {
    height: 1px;
    margin: 13px 0;
    background: #e8ecf2;
}

.movie-info {
    display: flex;
    gap: 12px;
}

.movie-poster {
    width: 72px;
    height: 96px;
    flex: none;
    overflow: hidden;
    border-radius: 8px;
    background: #f1f3f7;
}

.movie-detail {
    min-width: 0;
    flex: 1;
}

.movie-title-row {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    h3 {
        min-width: 0;
        margin: 0;
        overflow: hidden;
        color: #1d2735;
        font-size: 16px;
        line-height: 1.4;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    button {
        min-height: 30px;
        flex: none;
        padding: 0 4px;
        border: 0;
        background: transparent;
        color: #5b5bd6;
        font-size: 12px;
    }
}

.showtime-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    color: #1d2735;
    font-size: 13px;
}

.today-tag {
    padding: 3px 6px;
    border-radius: 5px;
    background: #fff0f0;
    color: #e5484d;
    font-size: 11px;
}

.hall-name {
    margin-top: 7px;
    color: #657284;
    font-size: 13px;
}

.movie-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 9px;

    span {
        padding: 3px 7px;
        border-radius: 6px;
        background: #f1f3f7;
        color: #536171;
        font-size: 11px;
    }
}

.address-row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 14px;
    padding-top: 13px;
    border-top: 1px solid #e8ecf2;
    color: #657284;
    font-size: 13px;
    line-height: 1.5;

    .van-icon {
        flex: none;
        margin-top: 2px;
        color: #5b5bd6;
    }
}

.price-card {
    margin-top: 12px;
    padding: 5px 15px;
}

.price-row {
    display: flex;
    min-height: 47px;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    border-bottom: 1px solid #eef1f5;
    color: #657284;
    font-size: 14px;

    &:last-child {
        border-bottom: 0;
    }

    strong {
        color: #1d2735;
        font-weight: 600;
        text-align: right;
    }

    .price-limit {
        color: #e5484d;
    }
}

.current-price-row {
    strong {
        color: #e5484d;
        font-size: 16px;
    }
}

.bid-bar {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 20;
    padding: 9px 12px calc(10px + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid #e8ecf2;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 -8px 24px rgba(28, 42, 68, 0.07);
}

.bid-deadline {
    display: flex;
    max-width: 616px;
    min-height: 22px;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    margin: 0 auto 6px;
    color: #657284;
    font-size: 12px;

    ::v-deep .van-count-down {
        color: #e5484d;
        font-size: 13px;
        font-weight: 650;
    }

    &.is-expired {
        color: #8793a3;
    }
}

.bid-actions {
    display: flex;
    width: 100%;
    max-width: 616px;
    align-items: center;
    gap: 8px;
    margin: 0 auto;
}

.cancel-bid-btn {
    width: 70px;
    height: 46px;
    flex: none;
    border-color: #dfe4eb !important;
    color: #536171 !important;
}

.bid-field {
    display: flex;
    height: 46px;
    min-width: 72px;
    flex: 1;
    align-items: center;
    padding: 0 11px;
    border: 1px solid #dfe4eb;
    border-radius: 10px;
    background: #f8f9fb;

    &:focus-within {
        border-color: #5b5bd6;
        background: #fff;
        box-shadow: 0 0 0 3px rgba(91, 91, 214, 0.1);
    }

    &.is-disabled {
        background: #f1f3f5;
    }

    > span {
        color: #e5484d;
        font-size: 16px;
        font-weight: 650;
    }

    input {
        width: 100%;
        min-width: 0;
        height: 100%;
        padding-left: 3px;
        border: 0;
        outline: 0;
        background: transparent;
        color: #1d2735;
        font-size: 16px;
        font-weight: 650;
    }
}

.confirm-bid-btn {
    min-width: 92px;
    height: 46px;
    flex: none;
    padding: 0 12px;
    border-radius: 10px;
    font-weight: 650;
}

@media (max-width: 359px) {
    .order-detail-content {
        padding-right: 10px;
        padding-left: 10px;
    }

    .order-summary-card {
        padding: 13px;
    }

    .movie-poster {
        width: 66px;
        height: 90px;
    }

    .bid-bar {
        padding-right: 8px;
        padding-left: 8px;
    }

    .bid-actions {
        gap: 6px;
    }

    .cancel-bid-btn {
        width: 62px;
        padding: 0 8px;
    }

    .confirm-bid-btn {
        min-width: 82px;
        padding: 0 8px;
    }
}
</style>
